import { useMemo } from "react";
import { queries } from "../../shared/queries";
import type { SpaceMode } from "../../shared/spaceMode";
import { getWritableScopeIds } from "../lib/getWritableScopeIds";
import { useCurrentUserGrants } from "./useCurrentUserGrants";
import { useQuery } from "@rocicorp/zero/react";
import { useSpaceDisplayName } from "./useSpaceDisplayName";

export type MoveTarget = {
  id: string;
  name: string;
  mode: SpaceMode;
  disabled?: boolean;
  disabledReason?: string;
};

export function useMoveTargets(): MoveTarget[] {
  const [spaces] = useQuery(queries.spaces({ settingsView: true }));
  const [personalSpace] = useQuery(queries.personalSpace());

  const [myGrants] = useCurrentUserGrants();

  const displaySpaceName = useSpaceDisplayName();

  const personalSpaceId = personalSpace?.id;

  // MoveTargetsLoader publishes this array in an effect, so its identity must stay stable between renders.
  return useMemo(() => {
    const writableSpaceIds = getWritableScopeIds(
      myGrants,
      spaces.map((s) => s.id),
      "space:write"
    );
    return spaces
      .filter((s) => s.mode !== "personal" || s.id === personalSpaceId)
      .map((s) => {
        const name = displaySpaceName(s);
        if (!writableSpaceIds.has(s.id)) {
          return {
            id: s.id,
            mode: s.mode,
            name,
            disabled: true,
            disabledReason: "You don't have write access to this space",
          };
        }
        return { id: s.id, mode: s.mode, name };
      });
  }, [spaces, displaySpaceName, myGrants, personalSpaceId]);
}
